/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Hub 権限管理ページ。権限マトリクスタブとユーザー管理タブの 2 タブ構成。ロール CRUD・ユーザーロール割当を提供する。
 * @graph-connects /permissions/roles [calls] ロール CRUD
 * @graph-connects /permissions/user-roles [calls] ユーザーロール管理
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useRoles, useCreateRole, useUpdateRole, useDeleteRole } from '../hooks/useRoles.js';
import { UsersTab } from '../components/permissions/UsersTab.js';
import { ALL_PAGE_KEYS, NAV_GROUPS, PAGELESS_PERMISSIONS } from '../nav.js';
import type { Role } from '../types/permissions.js';

/** @graph-connects none */
const TD = 'border-b border-[var(--color-border)] p-2';
/** @graph-connects none */
const INPUT =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1 text-xs';

/** @graph-connects none */
export function PermissionsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'matrix' | 'users'>('matrix');

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6">
      <header>
        <p className="font-mono text-xs uppercase tracking-widest text-[var(--color-text-muted)]">
          Permissions
        </p>
        <h1 className="text-2xl font-bold">{t('permissions.title')}</h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('permissions.description')}
        </p>
      </header>

      <div className="flex gap-0 border-b border-[var(--color-border)]">
        <button
          type="button"
          className={`px-5 py-2.5 text-sm font-medium transition-colors ${activeTab === 'matrix' ? 'border-b-2 border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]' : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'}`}
          onClick={() => {
            setActiveTab('matrix');
          }}
        >
          {t('permissions.tabMatrix')}
        </button>
        <button
          type="button"
          className={`px-5 py-2.5 text-sm font-medium transition-colors ${activeTab === 'users' ? 'border-b-2 border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]' : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'}`}
          onClick={() => {
            setActiveTab('users');
          }}
        >
          {t('permissions.tabUsers')}
        </button>
      </div>

      {activeTab === 'matrix' ? <MatrixTab /> : <UsersTab />}
    </div>
  );
}

// ── Matrix Tab ──

/** @graph-connects none */
function MatrixTab() {
  const { t } = useTranslation();
  const { data: rolesData, isLoading } = useRoles();
  const updateRole = useUpdateRole();
  const createRole = useCreateRole();
  const deleteRoleMut = useDeleteRole();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [duplicateSource, setDuplicateSource] = useState<Role | null>(null);

  const roles = rolesData?.roles ?? [];
  const allKeys = ALL_PAGE_KEYS;

  const handleToggle = useCallback(
    (roleId: string, key: string, currentPermissions: string[]) => {
      const next = currentPermissions.includes(key)
        ? currentPermissions.filter((k) => k !== key)
        : [...currentPermissions, key];
      updateRole.mutate({ id: roleId, input: { permissions: next } });
    },
    [updateRole],
  );

  const handleGroupToggle = useCallback(
    (roleId: string, groupKeys: string[], currentPermissions: string[]) => {
      const allChecked = groupKeys.every((k) => currentPermissions.includes(k));
      const next = allChecked
        ? currentPermissions.filter((k) => !groupKeys.includes(k))
        : [...new Set([...currentPermissions, ...groupKeys])];
      updateRole.mutate({ id: roleId, input: { permissions: next } });
    },
    [updateRole],
  );

  const handleCreate = useCallback(() => {
    const permissions = duplicateSource?.permissions ?? [];
    createRole.mutate(
      { name: newRoleName, description: newRoleDesc, permissions },
      {
        onSuccess: () => {
          setShowCreateModal(false);
          setNewRoleName('');
          setNewRoleDesc('');
          setDuplicateSource(null);
        },
      },
    );
  }, [createRole, newRoleName, newRoleDesc, duplicateSource]);

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    deleteRoleMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
      },
    });
  }, [deleteRoleMut, deleteTarget]);

  if (isLoading) {
    return (
      <Card className="p-10 text-center text-sm text-[var(--color-text-muted)]">
        {t('app.loading')}
      </Card>
    );
  }

  return (
    <>
      <Card>
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-4 py-3">
          <Button
            variant="primary"
            className="gap-1 text-xs"
            onClick={() => {
              setDuplicateSource(null);
              setShowCreateModal(true);
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            {t('permissions.addRole')}
          </Button>
          {roles.length > 0 && (
            <div className="relative">
              <Button
                className="gap-1 text-xs"
                onClick={() => {
                  setDuplicateSource(roles[0]);
                  setShowCreateModal(true);
                }}
              >
                <Copy className="h-3.5 w-3.5" />
                {t('permissions.duplicateCreate')}
              </Button>
            </div>
          )}
          <span className="ml-auto text-xs text-[var(--color-text-muted)]">
            {t('permissions.roleAndPageCount', {
              roles: String(roles.length),
              pages: String(allKeys.length),
            })}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th
                  className={`${TD} sticky left-0 z-10 min-w-[200px] bg-[var(--color-bg-elevated)] text-left font-semibold`}
                >
                  {t('permissions.pageColumn')}
                </th>
                {roles.map((role) => (
                  <th
                    key={role.id}
                    className={`${TD} min-w-[110px] bg-[var(--color-bg-elevated)] text-center`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      {role.isSystem && (
                        <span className="rounded bg-[var(--color-accent-primary-alpha)] px-1.5 py-0.5 text-[9px] font-semibold text-[var(--color-accent-primary)]">
                          SYSTEM
                        </span>
                      )}
                      <span className="font-semibold">{role.name}</span>
                      <span className="font-normal text-[var(--color-text-muted)]">
                        {t('permissions.userCount', { num: String(role.userCount) })}
                      </span>
                      {!role.isSystem && (
                        <button
                          type="button"
                          className="text-[var(--color-text-muted)] hover:text-[var(--color-error)]"
                          onClick={() => {
                            setDeleteTarget(role);
                          }}
                          title={t('permissions.deleteRole')}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {NAV_GROUPS.map((group) => {
                // 追加権限（会員一覧のSTG権限など）も1行ずつ出す。列挙し忘れると
                // その権限を誰にも付与できないまま「持っていれば見える」だけのキーになる
                const permissionItems = group.items.flatMap((i) => [
                  { key: i.key, labelKey: i.labelKey },
                  ...(i.extraPermissions ?? []),
                ]);
                const groupKeys = permissionItems.map((i) => i.key);
                return (
                  <GroupSection
                    key={group.labelKey ?? 'top'}
                    labelKey={group.labelKey}
                    groupKeys={groupKeys}
                    items={permissionItems}
                    roles={roles}
                    onToggle={handleToggle}
                    onGroupToggle={handleGroupToggle}
                  />
                );
              })}
              {/* 画面を持たない機能の権限。ナビには出ないが、ここから付与できないと
                  API を叩く実行者（母艦の日次同期など）に権限を渡す手段が無くなる */}
              <GroupSection
                labelKey="nav.groupPageless"
                groupKeys={PAGELESS_PERMISSIONS.map((p) => p.key)}
                items={[...PAGELESS_PERMISSIONS]}
                roles={roles}
                onToggle={handleToggle}
                onGroupToggle={handleGroupToggle}
              />
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setDuplicateSource(null);
        }}
        title={
          duplicateSource
            ? t('permissions.duplicateRoleTitle', { name: duplicateSource.name })
            : t('permissions.addRole')
        }
        size="sm"
      >
        <div className="space-y-3 p-4">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-text-secondary)]">
              {t('permissions.roleName')}
            </label>
            <input
              className={INPUT}
              value={newRoleName}
              onChange={(e) => {
                setNewRoleName(e.target.value);
              }}
              placeholder={t('permissions.roleNamePlaceholder')}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-text-secondary)]">
              {t('permissions.roleDescription')}
            </label>
            <input
              className={INPUT}
              value={newRoleDesc}
              onChange={(e) => {
                setNewRoleDesc(e.target.value);
              }}
              placeholder={t('permissions.roleDescriptionPlaceholder')}
            />
          </div>
          {duplicateSource && (
            <p className="text-xs text-[var(--color-text-muted)]">
              {t('permissions.copiedFromNote', { name: duplicateSource.name })}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              onClick={() => {
                setShowCreateModal(false);
                setDuplicateSource(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!newRoleName.trim() || createRole.isPending}
            >
              {t('permissions.create')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        open={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
        }}
        title={t('permissions.deleteRoleConfirmTitle')}
        size="sm"
      >
        <div className="space-y-3 p-4">
          <p className="text-sm">
            {t('permissions.deleteRoleConfirm', { name: deleteTarget?.name ?? '' })}
            {(deleteTarget?.userCount ?? 0) > 0 && (
              <span className="mt-1 block text-xs text-[var(--color-error)]">
                {t('permissions.deleteRoleUserImpact', {
                  num: String(deleteTarget?.userCount ?? 0),
                })}
              </span>
            )}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setDeleteTarget(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              className="bg-[var(--color-error)] hover:bg-[var(--color-error)]"
              onClick={handleDelete}
              disabled={deleteRoleMut.isPending}
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ── Group Section ──

/** @graph-connects none */
function GroupSection({
  labelKey,
  groupKeys,
  items,
  roles,
  onToggle,
  onGroupToggle,
}: {
  labelKey?: string;
  groupKeys: string[];
  items: { key: string; labelKey: string }[];
  roles: Role[];
  onToggle: (roleId: string, key: string, perms: string[]) => void;
  onGroupToggle: (roleId: string, keys: string[], perms: string[]) => void;
}) {
  const { t } = useTranslation();
  const label = labelKey ? t(labelKey) : t('nav.groupTop');

  return (
    <>
      <tr>
        <td
          colSpan={1 + roles.length}
          className={`${TD} bg-[var(--color-bg-elevated)] text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]`}
        >
          <span className="flex items-center gap-2">
            {label}
            {roles.map((role) => (
              <button
                key={role.id}
                type="button"
                className="ml-1 text-[10px] font-medium normal-case tracking-normal text-[var(--color-accent-primary)] hover:underline"
                onClick={() => {
                  onGroupToggle(role.id, groupKeys, role.permissions);
                }}
                title={t('permissions.toggleGroup', { name: role.name })}
              >
                {groupKeys.every((k) => role.permissions.includes(k))
                  ? t('permissions.deselectAll')
                  : t('permissions.selectAll')}
              </button>
            ))}
          </span>
        </td>
      </tr>
      {items.map((item) => (
        <tr key={item.key} className="hover:bg-[var(--color-bg-hover)]">
          <td className={`${TD} pl-8`}>{t(item.labelKey)}</td>
          {roles.map((role) => (
            <td key={role.id} className={`${TD} text-center`}>
              <input
                type="checkbox"
                className="h-4 w-4 accent-[var(--color-accent-primary)]"
                checked={role.permissions.includes(item.key)}
                onChange={() => {
                  onToggle(role.id, item.key, role.permissions);
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
